import { UseQueryResult, useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import { useEnabledQuery } from '../../../../../ui/hooks/useEnabledQuery';
import { getMutationSchema } from '../../../../../ui/util/getMutationSchema';
import { ResourceType } from '../../../../enumerations/ResourceType';
import {
    StructuredVCycleObjectOutline,
    StructuredVCycleObjectOutlineSchema,
} from '../../../../outlines/vcycleElementOutline';
import { get } from '../../../../resource-base/APIVersion1';
import { parseRawData, useOnUnauthorized } from '../../../../resource-base/adapters/AdapterUtils';
import { useIndirectlyVersionedResourceAdapter } from '../../../../resource-base/adapters/useIndirectlyVersionedResourceAdapter';
import { useMultiResourceSingleParentedResourceAdapterActions } from '../../../../resource-base/adapters/useMultiResourceSingleParentedResourceAdapterActions';
import {
    defaultIndirectlyVersionedArtifactResource,
    IndirectlyVersionedArtifactResourceSchema,
} from '../../../../resource-base/artifactResourceTypes';
import { IdSchema } from '../../../../resource-base/baseSchemas';
import { ParamConstants } from '../../../../resource-base/types';

export enum GridElementPropertyValidation {
    X_INDEX = 'X_INDEX',
    Y_INDEX = 'Y_INDEX',
    HEIGHT = 'HEIGHT',
    WIDTH = 'WIDTH',
    PROCESS_AREA = 'PROCESS_AREA',
}

const GridElementResourceSchema = IndirectlyVersionedArtifactResourceSchema.extend({
    xIndex: z.number(),
    yIndex: z.number(),
    height: z.number(),
    width: z.number(),
    processArea: IdSchema.nullable(),
    resourceType: z.literal(ResourceType.GRID_ELEMENT).default(ResourceType.GRID_ELEMENT),
});

export const GridElementMutationResourceSchema = getMutationSchema(GridElementResourceSchema);

export type GridElementResource = z.infer<typeof GridElementResourceSchema>;

export const defaultGridElementResource: GridElementResource = {
    ...defaultIndirectlyVersionedArtifactResource,
    resourceType: ResourceType.GRID_ELEMENT,
    xIndex: 0,
    yIndex: 0,
    height: 1,
    width: 1,
    processArea: null,
};

export function useGridElementActions() {
    const onUnauthorized = useOnUnauthorized();
    const getEnabled = useEnabledQuery();
    const actions = {
        ...useMultiResourceSingleParentedResourceAdapterActions(
            'project/grid-element',
            ResourceType.GRID_ELEMENT,
            GridElementResourceSchema
        ),
        ...useIndirectlyVersionedResourceAdapter(
            'project/grid-element',
            ResourceType.GRID_ELEMENT,
            GridElementResourceSchema
        ),
    };

    const useGetStructuredGridElements = (parentId: string | undefined | null) => {
        return useQuery({
            queryKey: [parentId, ResourceType.V_CYCLE_ELEMENT_CONTAINER, 'STRUCTURED'],
            queryFn: () =>
                get(
                    `project/v-cycle-element-container/structured?${ParamConstants.PARENT_ID_REQUEST_PARAM_NAME}=${parentId}`
                ).then((result) => {
                    if (result.status === 200) {
                        return result
                            .json()
                            .then((outline) => parseRawData(outline, StructuredVCycleObjectOutlineSchema));
                    } else if (result.status === 401) {
                        return onUnauthorized(result, null).then((_) => {
                            throw Error('Unauthorized!');
                        });
                    }
                }),
            enabled: getEnabled(parentId),
        });
    };

    return {
        ...actions,
        useGetStructuredGridElements,
    };
}
